import { validatePolicyNumber } from 'Agent/Policies/ducks/policyValidation';
import {isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {constTrue} from 'fp-ts/lib/function';
import * as NEA from 'fp-ts/lib/NonEmptyArray';
import * as O from 'fp-ts/lib/Option';

export enum PremiumMode {
  Annual = 1,
  Semiannual = 2,
  PreauthorizedCheckPlan = 3,
  Quarterly = 4,
}

export enum ChangeType {
  BillingScheduleChange = 1,
  DirectBillingChange = 2,
  AddressChange = 3,
}

export type InvalidPolicyNumber = {
  id: string;
  value: StringUnion;
};

export type ValidPolicyNumber = {
  id: string;
  value: NonEmptyString;
};

export type PolicyNumber = InvalidPolicyNumber | ValidPolicyNumber;

const isPolicyNumberValid = (num: PolicyNumber): num is ValidPolicyNumber => validatePolicyNumber(num.value);

const areAllPolicyNumbersValid = (
  nums: NEA.NonEmptyArray<PolicyNumber>
): nums is NEA.NonEmptyArray<ValidPolicyNumber> => nums.every(isPolicyNumberValid);

export class InvalidInitialPolicyServiceRequest {
  public readonly key = 'InvalidInitialPolicyServiceRequest';

  constructor(
    public readonly policyNumber: StringUnion,
    public readonly insuredLastName: StringUnion,
    public readonly insuredFirstName: StringUnion,
    public readonly insuredMiddleName: StringUnion,
    public readonly additionalPolicyNumbers: PolicyNumber[],
    public readonly requestChangeType: O.Option<ChangeType>
  ) {}
}

export type InitialPolicyServiceRequest = ValidInitialPolicyServiceRequest | InvalidInitialPolicyServiceRequest;

export class ValidInitialPolicyServiceRequest {
  public static createFromExisting(serviceRequest: InitialPolicyServiceRequest): InitialPolicyServiceRequest {
    return this.create(
      serviceRequest.policyNumber,
      serviceRequest.insuredLastName,
      serviceRequest.insuredFirstName,
      serviceRequest.insuredMiddleName,
      serviceRequest.additionalPolicyNumbers,
      serviceRequest.requestChangeType
    );
  }

  public static create(
    policyNumber: StringUnion,
    insuredLastName: StringUnion,
    insuredFirstName: StringUnion,
    insuredMiddleName: StringUnion,
    additionalPolicyNumbers: PolicyNumber[],
    requestChangeType: O.Option<ChangeType>
  ): InitialPolicyServiceRequest {
    const policyNumbersOption = NEA.fromArray(additionalPolicyNumbers);

    return validatePolicyNumber(policyNumber) &&
      isNonEmptyString(insuredLastName) &&
      isNonEmptyString(insuredFirstName) &&
      O.fold(constTrue, areAllPolicyNumbersValid)(policyNumbersOption) &&
      O.isSome(requestChangeType)
      ? new ValidInitialPolicyServiceRequest(
          policyNumber,
          insuredLastName,
          insuredFirstName,
          insuredMiddleName,
          O.isSome(policyNumbersOption) && areAllPolicyNumbersValid(policyNumbersOption.value)
            ? policyNumbersOption.value
            : [],
          requestChangeType
        )
      : new InvalidInitialPolicyServiceRequest(
          policyNumber,
          insuredLastName,
          insuredFirstName,
          insuredMiddleName,
          additionalPolicyNumbers,
          requestChangeType
        );
  }

  public readonly key = 'ValidInitialPolicyServiceRequest';

  private constructor(
    public readonly policyNumber: NonEmptyString,
    public readonly insuredLastName: NonEmptyString,
    public readonly insuredFirstName: NonEmptyString,
    public readonly insuredMiddleName: StringUnion,
    public readonly additionalPolicyNumbers: ValidPolicyNumber[],
    public readonly requestChangeType: O.Some<ChangeType>
  ) {}
}

export const isValidInitialPolicyServiceRequest = (
  initialServiceRequest: InitialPolicyServiceRequest
): initialServiceRequest is ValidInitialPolicyServiceRequest =>
  initialServiceRequest.key === 'ValidInitialPolicyServiceRequest';
